import {defineStore} from 'pinia'
import axios from '@/api/axios'

export const useCompanyBillingStore = defineStore({
  id: 'companyBillingStore',
  actions: {
    async getQuickBooksPayments(company_hash, quick_books_customer_id) {
      const {data} = await axios.get('quick-books/payments', {
        params: {
          company_hash,
          quick_books_customer_id,
        },
      })

      return data
    },

    async getAllQuickBooksPayments(company_hash, quick_books_customer_id) {
      const {data} = await axios.get('quick-books/payments/all', {
        params: {
          company_hash,
          quick_books_customer_id,
        },
      })

      return data // { book_close_date, payments: [{..., can_be_auto_applied}] }
    },

    async getQuickBooksCustomers(company_hash) {
      const {data} = await axios.get('quick-books-customers', {params: {company_hash}})

      return data
    },

    async getQuickBooksServices() {
      const {data} = await axios.get('quick-books/services')

      return data.services // [{id, name}] for the active (prod/sandbox) environment
    },

    // Invoices

    async getInvoiceInfo(id, params) {
      const {data} = await axios.get(`company-invoices/${id}`, {params})

      return data
    },

    async getInvoices(filters) {
      const {data} = await axios.get('company-invoices', {params: filters})

      return data
    },

    async getInvoicesSummary(params) {
      const {data} = await axios.get('company-invoices/summary', {params})

      return data
    },

    async getInvoiceRequisites(payment_type) {
      const {data} = await axios.get('company-invoices/requisites', {params: {payment_type}})

      return {requisites: data.requisites, tag: data.tag ?? null}
    },

    async getInvoiceTerms() {
      const {data} = await axios.get('company-invoices/terms')

      return data.terms // [{id, name, duedays}]
    },

    async createInvoice(payload, params) {
      const {data} = await axios.post('company-invoices', payload, {params})

      return data
    },

    async generateInvoices(payload) {
      const {data} = await axios.post('company-invoices/generate', payload)

      return data
    },

    async getInvoiceRequisitesPublic(payment_type) {
      const {data} = await axios.get('public/company-invoices/requisites', {params: {payment_type}})

      return {requisites: data.requisites, tag: data.tag ?? null}
    },

    async sendInvoiceEmail(hash, email) {
      const {data} = await axios.post(`company-invoices/${hash}/send`, {email})

      return data
    },

    async resendInvoiceEmail(hash, email) {
      const {data} = await axios.post(`company-invoices/${hash}/resend-email`, {email})

      return data
    },

    async createProformaInvoicePublic(payload, params) {
      const {data} = await axios.post('public/company-invoices', payload, {params})

      return data
    },

    async updateInvoice(payload) {
      const {data} = await axios.patch(`company-invoices/${payload.id}`, payload)

      return data
    },

    async linkInvoicePayments(hash, payments) {
      const {data} = await axios.post(`company-invoices/${hash}/link-payments`, {payments})

      return data
    },

    async refreshInvoiceBalance(hash) {
      const {data} = await axios.post(`company-invoices/${hash}/refresh-balance`)

      return data
    },

    async destroyInvoice(id) {
      const {data} = await axios.delete(`company-invoices/${id}`)

      return data
    },

    async bulkDestroyInvoices(hashes) {
      const {data} = await axios.post('company-invoices/bulk-destroy', {hashes})

      return data
    },

    // Payments

    async getPaymentsSummary(params) {
      const {data} = await axios.get('company-payments/summary', {params})

      return data
    },

    async confirmPayment(hash, payload) {
      const {data} = await axios.patch(`company-payments/${hash}/confirm`, payload)

      return data
    },

    async createPayment(payload, params) {
      const {data} = await axios.post(`company-payments`, payload, {params})

      return data
    },

    async getPaymentInfo(id, params) {
      const {data} = await axios.get(`company-payments/${id}`, {params})

      return data
    },

    async getPayments(filters = {}) {
      const {data} = await axios.get('company-payments', {params: filters})

      return data
    },

    async destroyPayment(id) {
      const {data} = await axios.delete(`company-payments/${id}`)

      return data
    },

    async createOverdraft(payload, params = {}) {
      const {data} = await axios.post(`overdraft-requests`, payload, {params})

      return data
    },
    async getOverdraftRequests(payload = {}) {
      const {data} = await axios.get(`overdraft-requests`, {params: payload})

      return data
    },
  },
})
